// node modules
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Tooltip from 'rc-tooltip';
import withStyles from 'isomorphic-style-loader/withStyles';
import Button from '@material-ui/core/Button';
import FavoriteBorderOutlinedIcon from '@material-ui/icons/FavoriteBorderOutlined';
import FavoriteOutlinedIcon from '@material-ui/icons/FavoriteOutlined';

// app modules: actions
import { addFavouriteContent, removeFavouriteContent } from 'actions/favourites';
import { featureUsed } from 'actions/featureUsage';

// app modules: props
import LoginProps from 'components/Login/LoginProps';

// app modules: services
import history from 'services/history';

// app modules: constants
import { CONTENT_TYPES, TOOLTIP_DELAY_MS } from 'constants/index';

// assets
import CssFavouriteButton from './FavouriteButton.scss';

export class FavouriteButtonComponent extends Component {
  static propTypes = {
    contentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    hash: PropTypes.string,
    contentType: PropTypes.oneOf([
      CONTENT_TYPES.RECIPE,
      CONTENT_TYPES.REFERENCE,
      CONTENT_TYPES.SECTION,
      CONTENT_TYPES.PERSON,
      CONTENT_TYPES.BOOK,
      CONTENT_TYPES.COLLECTION,
    ]).isRequired,
    className: PropTypes.string,
    buttonClassName: PropTypes.string,
    favourites: PropTypes.shape({
      recipe: PropTypes.arrayOf(PropTypes.string),
      reference: PropTypes.arrayOf(PropTypes.string),
      section: PropTypes.arrayOf(PropTypes.string),
      book: PropTypes.arrayOf(PropTypes.string),
      person: PropTypes.arrayOf(PropTypes.string),
    }),
    addFavouriteContent: PropTypes.func.isRequired,
    removeFavouriteContent: PropTypes.func.isRequired,
    featureUsed: PropTypes.func.isRequired,
    user: LoginProps.isRequired,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    favourites: {},
    className: '',
    buttonClassName: '',
    hash: '',
  };

  constructor(props) {
    super(props);

    this.state = {
      tooltipVisible: false,
      wasClicked: false,
    };

    this.bound = {
      onClickFavourite: this.onClickFavourite.bind(this),
      onMouseLeave: this.onMouseLeave.bind(this),
      onMouseEnter: this.onMouseEnter.bind(this),
    };
  }

  onMouseEnter() {
    this.tooltipDisplayTimeout = setTimeout(
      () => this.setState({ tooltipVisible: true }),
      TOOLTIP_DELAY_MS,
    );
  }

  onMouseLeave() {
    this.setState({ tooltipVisible: false, wasClicked: false });
    if (this.tooltipDisplayTimeout) {
      clearTimeout(this.tooltipDisplayTimeout);
      this.tooltipDisplayTimeout = null;
    }
  }

  onClickFavourite() {
    const { contentId, hash, contentType, user } = this.props;

    if (user.logged_in) {
      this.setState({ tooltipVisible: false, wasClicked: true });

      this.props.featureUsed('feature_favourite');

      if (this.isFavourite()) {
        this.props.removeFavouriteContent(`${contentId}`, contentType);
      } else {
        this.props.addFavouriteContent(`${contentId}`, hash, contentType);
      }
    } else {
      history.push('/login', { returnTo: history.location });
    }
  }

  isFavourite() {
    const { contentId, contentType, favourites } = this.props;
    if (favourites && favourites[contentType]) {
      return favourites[contentType].indexOf(`${contentId}`) > -1;
    }
    return false;
  }

  actionOverlay() {
    const { t } = this.props;
    const actionText = this.isFavourite()
      ? `Remove from ${t('favorites')}`
      : `Add to ${t('favorites')}`;
    return <div className="favourite-button__tooltip">{actionText}</div>;
  }

  render() {
    const { className, buttonClassName, t } = this.props;
    const { wasClicked, tooltipVisible } = this.state;
    const isFavourite = this.isFavourite();
    const icon = isFavourite ? (
      <FavoriteOutlinedIcon color="primary" fontSize="large" className="favourite-button__icon" />
    ) : (
      <FavoriteBorderOutlinedIcon
        color="primary"
        fontSize="large"
        className="favourite-button__icon"
      />
    );

    return (
      <div className={className}>
        <Tooltip
          overlay={this.actionOverlay()}
          placement="bottom"
          visible={tooltipVisible && !wasClicked}
        >
          <Button
            className={`allow-arrow-keys ${buttonClassName}`}
            onClick={this.bound.onClickFavourite}
            onMouseEnter={this.bound.onMouseEnter}
            onMouseLeave={this.bound.onMouseLeave}
            aria-label={
              this.isFavourite() ? `Remove from ${t('favorites')}` : `Add to ${t('favorites')}`
            }
          >
            {icon}
          </Button>
        </Tooltip>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  favourites: state.favourites,
  user: state.user,
});

const mapDispatchToProps = {
  addFavouriteContent,
  removeFavouriteContent,
  featureUsed,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(withStyles(CssFavouriteButton)(FavouriteButtonComponent)));
